<template>
  <div class="dash-root">
    <!-- ==================== 骨架屏 ==================== -->
    <div v-if="!dataReady" class="dash-shell">
      <div class="shell-hero">
        <div class="shell-block shell-block-lg"></div>
        <div class="shell-row">
          <div class="shell-block shell-block-sm"></div>
          <div class="shell-block shell-block-sm"></div>
        </div>
      </div>
      <div class="shell-label"></div>
      <div class="shell-grid-2">
        <div class="shell-card" v-for="i in 2" :key="'s2-'+i"></div>
      </div>
      <div class="shell-label"></div>
      <div class="shell-grid">
        <div class="shell-ext" v-for="i in 8" :key="i"></div>
      </div>
    </div>

    <!-- ==================== 主内容 ==================== -->
    <div v-show="dataReady">
      <!-- Hero 总览 -->
      <div class="dash-hero">
        <div class="hero-inner">
          <div class="hero-bg-glow"></div>
          <div class="hero-content">
          <div class="hero-head">
            <div class="hero-pill">
              <span class="pill-dot"></span>
              实时监控
            </div>
            <div class="hero-total">
              <span class="hero-yen">¥</span>
              <span class="hero-amount">{{ displayTotal }}</span>
              <span v-if="yesterdayGrandTotal > 0" :class="['hero-hb', grandTotal >= yesterdayGrandTotal ? 'hb-up' : 'hb-down']">
                {{ grandTotal >= yesterdayGrandTotal ? '↑' : '↓' }}
                {{ Math.abs(yesterdayGrandTotal > 0 ? parseFloat(((grandTotal - yesterdayGrandTotal) / yesterdayGrandTotal * 100).toFixed(1)) : 0) }}%
              </span>
            </div>
            <div class="hero-yest">
              <span class="yest-label">昨日同段</span>
              <span class="yest-num">¥{{ formatNum(yesterdayGrandTotal) }}</span>
              <span class="yest-divider">|</span>
              <span class="yest-label">昨日全天</span>
              <span class="yest-num">¥{{ formatNum(yesterdayGrandTotalFull) }}</span>
            </div>
            <div class="hero-sub-row">
              <span class="hero-sub-text">{{ lastUpdate }}</span>
              <span class="hero-auto-tag">自动刷新 · 5分钟</span>
            </div>
          </div>
          <div class="hero-kpis">
            <div class="hero-ctl">
              <span class="hero-clock">{{ currentTime }}</span>
              <span class="hero-refresh" @click="refreshAll">
                <i :class="['el-icon-refresh', { 'is-spin': refreshing }]"></i>
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- ==================== 内媒 ==================== -->
      <div class="dash-section">
        <div class="sec-head">
          <div class="sec-badge badge-int">内媒</div>
          <span class="sec-title">浙江 + 福建 核代汇总</span>
        </div>

        <div v-if="internalItem" class="intel-card">
          <div class="intel-card-glow"></div>
          <div class="intel-card-inner">
            <div class="intel-top">
              <div class="intel-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
              </div>
              <div class="intel-top-info">
                <span class="intel-top-label">内媒累计消耗</span>
                <span class="intel-top-sub">浙江 + 福建 核代</span>
              </div>
            </div>
            <div class="intel-body">
              <div class="intel-num">¥{{ formatNum(internalItem.totalConsume) }}</div>
              <span v-if="internalItem && internalItem.consumeHb" :class="['intel-hb', internalItem.consumeHb >= 0 ? 'hb-up' : 'hb-down']">
                <span class="hb-arrow">{{ internalItem.consumeHb >= 0 ? '↑' : '↓' }}</span>
                较昨日同段 {{ Math.abs(parseFloat(internalItem.consumeHb).toFixed(1)) }}%
              </span>
            </div>
            <div class="intel-extra">
              <div class="intel-extra-item">
                <span class="ie-label">昨日同段内媒</span>
                <span class="ie-val">¥{{ formatNum(yesterdayInternalTotal) }}</span>
              </div>
              <div class="intel-extra-item">
                <span class="ie-label">昨日全天内媒</span>
                <span class="ie-val">¥{{ formatNum(yesterdayInternalTotalFull) }}</span>
              </div>
              <div class="intel-extra-item">
                <span class="ie-label">数据更新时间</span>
                <span class="ie-val">{{ lastUpdate }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== 外媒 ==================== -->
      <div class="dash-section">
        <div class="sec-head">
          <div class="sec-badge badge-ext">外媒</div>
          <span class="sec-title">各渠道独立监控</span>
        </div>

        <!-- 外媒汇总卡片 -->
        <div class="extel-card">
          <div class="extel-card-glow"></div>
          <div class="extel-card-inner">
            <div class="extel-top">
              <div class="extel-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </div>
              <div class="extel-top-info">
                <span class="extel-top-label">外媒累计消耗</span>
                <span class="extel-top-sub">各渠道独立监控</span>
              </div>
            </div>
            <div class="extel-body">
              <div class="extel-num">¥{{ formatNum(externalTotal) }}</div>
              <span v-if="yesterdayExternalTotal > 0" :class="['extel-hb', externalTotal >= yesterdayExternalTotal ? 'hb-up' : 'hb-down']">
                <span class="hb-arrow">{{ externalTotal >= yesterdayExternalTotal ? '↑' : '↓' }}</span>
                较昨日同段 {{ Math.abs(parseFloat(((externalTotal - yesterdayExternalTotal) / yesterdayExternalTotal * 100).toFixed(1))) }}%
              </span>
            </div>
            <div class="extel-extra">
              <div class="extel-extra-item">
                <span class="ee-label">昨日同段外媒</span>
                <span class="ee-val">¥{{ formatNum(yesterdayExternalTotal) }}</span>
              </div>
              <div class="extel-extra-item">
                <span class="ee-label">昨日全天外媒</span>
                <span class="ee-val">¥{{ formatNum(yesterdayExternalTotalFull) }}</span>
              </div>
              <div class="extel-extra-item">
                <span class="ee-label">渠道数量</span>
                <span class="ee-val">{{ externalList.length }} 个</span>
              </div>
            </div>
          </div>
        </div>

        <div class="ext-grid">
          <div v-for="item in externalList" :key="item.sourceType" class="ext-cell">
            <div :class="['ext-top', getTopClass(item.sourceType)]">
              <div class="ext-top-inner">
                <span class="ext-name">{{ item.sourceName }}</span>
                <div class="ext-val-row">
                  <span class="ext-val">¥{{ formatNum(item.totalConsume) }}</span>
                  <span v-if="item.consumeHb"
                    :class="['ext-hb', item.consumeHb >= 0 ? 'hb-up' : 'hb-down']">
                    {{ item.consumeHb > 0 ? '+' : '' }}{{ parseFloat(item.consumeHb).toFixed(1) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== 底部 ==================== -->
      <div class="dash-footer">
        <span>vivo</span><span class="ft-dot">·</span>
        <span>OPPO</span><span class="ft-dot">·</span>
        <span>快手</span><span class="ft-dot">·</span>
        <span>腾讯</span><span class="ft-dot">·</span>
        <span>票圈</span><span class="ft-dot">·</span>
        <span>爱奇艺</span><span class="ft-dot">·</span>
        <span>WiFi</span>
        <span class="ft-gap"></span>
        <span class="ft-live"><i class="ft-pulse"></i> 数据在线</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

var API_BASE = 'https://wcry.zjwec.cn'

// ==================== 数字缓动动画 ====================
function animateNum(startVal, endVal, callback, duration) {
  var diff = endVal - startVal
  if (Math.abs(diff) < 1) { callback(Math.round(endVal)); return }
  var dur = duration || 600
  var startTime = null
  function easeOut(t) { return 1 - Math.pow(1 - t, 3) }
  function step(ts) {
    if (!startTime) startTime = ts
    var p = Math.min((ts - startTime) / dur, 1)
    var val = startVal + diff * easeOut(p)
    callback(Math.round(val))
    if (p < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

// ==================== 金额格式化 — 纯整数 ====================
function toWan(v) {
  var n = parseFloat(v) || 0
  return Math.round(n).toLocaleString('zh-CN')
}

function toNum(v) {
  if (v === null || v === undefined || v === '') return '--'
  var n = parseFloat(v)
  if (isNaN(n)) return '--'
  return Math.round(n).toLocaleString('zh-CN')
}

export default {
  name: 'WeichiAllDataNew',
  data: function() {
    return {
      dataReady: false,
      refreshing: false,
      currentTime: '',
      lastUpdate: '加载中...',
      refreshTimer: null,
      timeTimer: null,

      // 今日数据
      grandTotal: 0,
      internalTotal: 0,
      externalTotal: 0,
      internalItem: null,
      externalList: [],

      // 昨日对比数据
      yesterdayGrandTotal: 0,
      yesterdayInternalTotal: 0,
      yesterdayExternalTotal: 0,
      yesterdayGrandTotalFull: 0,
      yesterdayInternalTotalFull: 0,
      yesterdayExternalTotalFull: 0,

      // 动画显示值
      displayTotal: '0',
      displayInternal: '0',
      displayExternal: '0',

      // 环比缓存
      hbCache: {}
    }
  },

  created: function() {
    var self = this
    this.updateTime()
    this.timeTimer = setInterval(function() { self.updateTime() }, 10000)
    this.fetchAllData()
    this.refreshTimer = setInterval(function() { self.fetchAllData() }, 300000)
  },

  beforeDestroy: function() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    if (this.timeTimer) clearInterval(this.timeTimer)
  },

  methods: {
    // ==================== 时间 ====================
    updateTime: function() {
      var d = new Date()
      this.currentTime = [d.getHours(), d.getMinutes(), d.getSeconds()]
        .map(function(v) { return v.toString().padStart(2, '0') })
        .join(':')
    },

    // ==================== 数据获取 ====================
    refreshAll: function() {
      if (this.refreshing) return
      this.refreshing = true
      var self = this
      this.fetchAllData().finally(function() { self.refreshing = false })
    },

    fetchAllData: function() {
      var self = this

      return axios.get(API_BASE + '/dashboard/summary').then(function(res) {
        var d = res.data
        if (d.code === 200 && d.data) {
          var dd = d.data
          animateNum(self.grandTotal, dd.grandTotal || 0, function(v) { self.displayTotal = toWan(v) })
          animateNum(self.internalTotal, dd.internalTotal || 0, function(v) { self.displayInternal = toWan(v) })
          animateNum(self.externalTotal, dd.externalTotal || 0, function(v) { self.displayExternal = toWan(v) })

          self.grandTotal = dd.grandTotal || 0
          self.internalTotal = dd.internalTotal || 0
          self.externalTotal = dd.externalTotal || 0
          self.internalItem = dd.internal || null
          self.externalList = dd.external || []

          self.yesterdayGrandTotal = dd.yesterdayGrandTotal || 0
          self.yesterdayInternalTotal = dd.yesterdayInternalTotal || 0
          self.yesterdayExternalTotal = dd.yesterdayExternalTotal || 0
          self.yesterdayGrandTotalFull = dd.yesterdayGrandTotalFull || 0
          self.yesterdayInternalTotalFull = dd.yesterdayInternalTotalFull || 0
          self.yesterdayExternalTotalFull = dd.yesterdayExternalTotalFull || 0

          var now = new Date()
          self.lastUpdate = '更新于 ' + now.getHours().toString().padStart(2, '0') + ':' +
            now.getMinutes().toString().padStart(2, '0')

          self.dataReady = true
        }
      }).catch(function(err) {
        console.error('数据获取失败:', err)
      })
    },

    // ==================== 格式化 ====================
    formatNum: function(v) { return toNum(v) },

    getTopClass: function(sourceType) {
      var map = {
        'vivo_cpa': 'ext-top-0',
        'oppo_cpa_zj': 'ext-top-1',
        'oppo_sanzu_jq': 'ext-top-2',
        'ks': 'ext-top-3',
        'tencent': 'ext-top-4',
        'pq': 'ext-top-5',
        'iqiyi': 'ext-top-6',
        'oppo_chenhui': 'ext-top-7',
        'wifi': 'ext-top-8'
      }
      return map[sourceType] || 'ext-top-0'
    }
  }
}
</script>

<style scoped>
/* ================================================================
   全局 Reset & 变量
   ================================================================ */
.dash-root {
  --bg-deep: #090d1a;
  --bg-card: #111627;
  --bg-card-hover: #161d33;
  --border-card: rgba(255,255,255,0.05);
  --text-prime: #f1f5f9;
  --text-second: #94a3b8;
  --text-mute: #64748b;
  --accent: #63b3ed;
  --accent-green: #4ade80;
  --accent-red: #f87171;
  --accent-purple: #c084fc;
  --shadow-lg: 0 12px 40px rgba(0,0,0,0.35);
  --radius-xl: 20px;
  --radius-lg: 16px;
  --radius-md: 12px;
  --radius-sm: 10px;
  --gap: 20px;
  --gap-sm: 12px;

  font-family: -apple-system, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", sans-serif;
  background: var(--bg-deep);
  min-height: 100vh;
  padding: 16px 16px 32px;
  box-sizing: border-box;
  color: var(--text-prime);
  -webkit-font-smoothing: antialiased;
}

/* 深色主题文字选中 */
.dash-root ::selection { background: rgba(99,179,237,0.35); color: #fff; }
.dash-root ::-moz-selection { background: rgba(99,179,237,0.35); color: #fff; }

/* ================================================================
   骨架屏
   ================================================================ */
.dash-shell { max-width: 1200px; margin: 0 auto; }
.shell-hero {
  background: var(--bg-card);
  border-radius: var(--radius-xl);
  padding: 24px;
  margin-bottom: var(--gap);
  border: 1px solid var(--border-card);
}
.shell-block {
  background: linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.6s infinite;
  border-radius: 8px;
}
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.shell-block-lg { width: 180px; height: 36px; margin-bottom: 12px; }
.shell-row { display: flex; gap: 24px; }
.shell-block-sm { width: 90px; height: 20px; }
.shell-label { width: 80px; height: 16px; margin: 24px 0 12px; background: var(--bg-card); border-radius: 8px; }
.shell-card {
  height: 110px; background: var(--bg-card);
  border-radius: var(--radius-lg); border: 1px solid var(--border-card);
}
.shell-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: var(--gap-sm); }
.shell-grid-2 { display: grid; grid-template-columns: repeat(2,1fr); gap: var(--gap-sm); margin-bottom: var(--gap-sm); }
.shell-ext {
  height: 120px; background: var(--bg-card);
  border-radius: var(--radius-lg); border: 1px solid var(--border-card);
}

/* ================================================================
   Hero 总览区
   ================================================================ */
.dash-hero {
  max-width: 1200px;
  margin: 0 auto var(--gap);
  background: linear-gradient(135deg, #0f172a 0%, #1a2744 40%, #16203e 100%);
  border-radius: var(--radius-xl);
  border: 1px solid rgba(99,179,237,0.12);
  position: relative;
  box-shadow: var(--shadow-lg);
}
.hero-inner {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
}
.hero-bg-glow {
  position: absolute; top: -80px; right: -80px;
  width: 320px; height: 320px;
  background: radial-gradient(circle, rgba(99,179,237,0.1) 0%, transparent 70%);
  border-radius: 50%;
}
.hero-content {
  position: relative; z-index: 1;
  padding: 28px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}
.hero-head { flex-shrink: 0; }
.hero-pill {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(99,179,237,0.1); border: 1px solid rgba(99,179,237,0.2);
  padding: 3px 12px; border-radius: 20px;
  font-size: 11px; color: var(--accent); margin-bottom: 10px;
}
.pill-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 6px rgba(74,222,128,0.6); }
.hero-total {
  display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap;
}
.hero-yen { font-size: 20px; font-weight: 400; color: var(--text-mute); }
.hero-amount { font-size: 44px; font-weight: 900; letter-spacing: -2px; color: #fff; line-height: 1.1; font-variant-numeric: tabular-nums; }
.hero-hb {
  font-size: 15px; font-weight: 700; padding: 2px 8px; border-radius: 6px; margin-left: 4px;
  letter-spacing: 0;
}
.hb-up { color: var(--accent-green); background: rgba(74,222,128,0.08); }
.hb-down { color: var(--accent-red); background: rgba(248,113,113,0.08); }
.hero-sub-row { display: flex; align-items: center; gap: 14px; margin-top: 8px; }
.hero-sub-text { font-size: 12px; color: var(--text-mute); }
.hero-auto-tag { font-size: 10px; color: var(--text-mute); background: rgba(255,255,255,0.04); padding: 2px 10px; border-radius: 10px; }
.hero-yest { display: flex; align-items: baseline; gap: 8px; margin-top: 6px; }
.yest-label { font-size: 11px; color: var(--text-mute); }
.yest-num { font-size: 16px; font-weight: 600; color: var(--text-second); font-variant-numeric: tabular-nums; }
.yest-divider { color: rgba(255,255,255,0.1); margin: 0 4px; }

.hero-kpis { display: flex; align-items: center; gap: 24px; }
.hero-kpi { display: flex; align-items: center; gap: 10px; }
.kpi-ring {
  width: 42px; height: 42px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.kpi-internal .kpi-ring { background: rgba(99,179,237,0.1); }
.kpi-external .kpi-ring { background: rgba(74,222,128,0.1); }
.kpi-dot-int { width: 10px; height: 10px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 10px rgba(99,179,237,0.5); }
.kpi-dot-ext { width: 10px; height: 10px; border-radius: 50%; background: var(--accent-green); box-shadow: 0 0 10px rgba(74,222,128,0.5); }
.kpi-info { display: flex; flex-direction: column; }
.kpi-label {
  font-size: 15px; font-weight: 700; letter-spacing: 3px; margin-bottom: 2px;
}
.kpi-internal .kpi-label { color: #93c5fd; }
.kpi-external .kpi-label { color: #6ee7b7; }
.kpi-num { font-size: 18px; font-weight: 700; color: #fff; font-variant-numeric: tabular-nums; }

.hero-divider { width: 1px; height: 36px; background: rgba(255,255,255,0.08); }

.hero-ctl { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.hero-clock { font-size: 12px; color: var(--text-mute); font-variant-numeric: tabular-nums; }
.hero-refresh {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.06);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 16px; color: var(--text-mute);
  transition: all 0.25s;
}
.hero-refresh:hover { background: rgba(99,179,237,0.1); border-color: rgba(99,179,237,0.25); color: var(--accent); }
.hero-refresh .is-spin { animation: dash-spin 0.8s linear infinite; }
@keyframes dash-spin { to { transform: rotate(360deg); } }

/* ================================================================
   区块标题
   ================================================================ */
.dash-section { max-width: 1200px; margin: 0 auto var(--gap); }
.sec-head { display: flex; align-items: center; gap: 10px; margin-bottom: var(--gap-sm); }
.sec-badge {
  font-size: 13px; font-weight: 700; padding: 4px 14px; border-radius: 8px;
  letter-spacing: 1px;
}
.badge-int { background: rgba(99,179,237,0.12); color: var(--accent); }
.badge-ext { background: rgba(74,222,128,0.1); color: var(--accent-green); }
.sec-title { font-size: 13px; color: var(--text-mute); font-weight: 500; }
.sec-yest { font-size: 11px; color: var(--text-second); margin-left: auto; }

/* ================================================================
   内媒卡片 — 增强版
   ================================================================ */
.intel-card {
  background: linear-gradient(135deg, #0f1d3a 0%, #142750 40%, #111d35 100%);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(99,179,237,0.15);
  position: relative;
  overflow: hidden;
}
.intel-card-glow {
  position: absolute; top: -60px; left: -60px;
  width: 200px; height: 200px;
  background: radial-gradient(circle, rgba(99,179,237,0.08) 0%, transparent 70%);
  border-radius: 50%; pointer-events: none;
}
.intel-card-inner {
  position: relative; z-index: 1;
  padding: 28px 30px;
}

/* top bar */
.intel-top {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(99,179,237,0.08);
}
.intel-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba(99,179,237,0.12);
  display: flex; align-items: center; justify-content: center;
  color: var(--accent); flex-shrink: 0;
}
.intel-icon svg { width: 22px; height: 22px; }
.intel-top-info { display: flex; flex-direction: column; gap: 2px; }
.intel-top-label { font-size: 15px; font-weight: 700; color: #e2e8f0; letter-spacing: 0.5px; }
.intel-top-sub { font-size: 12px; color: var(--text-mute); }

/* main number */
.intel-body {
  display: flex; align-items: baseline; gap: 16px;
  flex-wrap: wrap; margin-bottom: 18px;
}
.intel-num {
  font-size: 48px; font-weight: 900; letter-spacing: -2px; color: #fff;
  line-height: 1; font-variant-numeric: tabular-nums;
}

.intel-hb {
  font-size: 14px; font-weight: 700; padding: 4px 12px; border-radius: 8px;
  display: inline-flex; align-items: center; gap: 4px;
}
.hb-arrow { font-size: 16px; }

/* extra row */
.intel-extra {
  display: flex; gap: 28px; flex-wrap: wrap;
}
.intel-extra-item { display: flex; flex-direction: column; gap: 4px; }
.ie-label { font-size: 11px; color: var(--text-mute); text-transform: uppercase; letter-spacing: 0.5px; }
.ie-val { font-size: 14px; font-weight: 600; color: var(--text-second); font-variant-numeric: tabular-nums; }

/* ================================================================
   外媒汇总卡片 — 增强版（绿色调）
   ================================================================ */
.extel-card {
  background: linear-gradient(135deg, #0f2d1a 0%, #143a28 40%, #11261c 100%);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(74,222,128,0.15);
  position: relative;
  overflow: hidden;
  margin-bottom: var(--gap-sm);
}
.extel-card-glow {
  position: absolute; top: -60px; right: -60px;
  width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(74,222,128,0.08) 0%, transparent 70%);
  border-radius: 50%; pointer-events: none;
}
.extel-card-inner {
  position: relative; z-index: 1;
  padding: 28px 30px;
}

/* top bar */
.extel-top {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(74,222,128,0.08);
}
.extel-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: rgba(74,222,128,0.12);
  display: flex; align-items: center; justify-content: center;
  color: var(--accent-green); flex-shrink: 0;
}
.extel-icon svg { width: 22px; height: 22px; }
.extel-top-info { display: flex; flex-direction: column; gap: 2px; }
.extel-top-label { font-size: 15px; font-weight: 700; color: #e2e8f0; letter-spacing: 0.5px; }
.extel-top-sub { font-size: 12px; color: var(--text-mute); }

/* main number */
.extel-body {
  display: flex; align-items: baseline; gap: 16px;
  flex-wrap: wrap; margin-bottom: 18px;
}
.extel-num {
  font-size: 48px; font-weight: 900; letter-spacing: -2px; color: #fff;
  line-height: 1; font-variant-numeric: tabular-nums;
}
.extel-hb {
  font-size: 14px; font-weight: 700; padding: 4px 12px; border-radius: 8px;
  display: inline-flex; align-items: center; gap: 4px;
}

/* extra row */
.extel-extra {
  display: flex; gap: 28px; flex-wrap: wrap;
}
.extel-extra-item { display: flex; flex-direction: column; gap: 4px; }
.ee-label { font-size: 11px; color: var(--text-mute); text-transform: uppercase; letter-spacing: 0.5px; }
.ee-val { font-size: 14px; font-weight: 600; color: var(--text-second); font-variant-numeric: tabular-nums; }

/* ================================================================
   外媒网格 — 无图表紧凑卡片
   ================================================================ */
.ext-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--gap-sm);
}
.ext-cell {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-card);
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.25s;
}
.ext-cell:hover {
  border-color: rgba(255,255,255,0.1);
  box-shadow: 0 6px 24px rgba(0,0,0,0.25);
  transform: translateY(-2px);
}

.ext-top { padding: 18px; position: relative; overflow: hidden; }
.ext-top::after {
  content: ''; position: absolute; top: -40px; right: -40px;
  width: 120px; height: 120px;
  background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
  border-radius: 50%;
}
.ext-top-inner { position: relative; z-index: 1; }
.ext-name { font-size: 13px; font-weight: 600; opacity: 0.9; display: block; margin-bottom: 8px; }
.ext-val-row { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.ext-val { font-size: 26px; font-weight: 800; font-variant-numeric: tabular-nums; }
.ext-hb {
  font-size: 13px; font-weight: 700; padding: 2px 8px; border-radius: 5px;
}

/* 各渠道顶部配色 */
.ext-top-0 { background: linear-gradient(135deg, #1e3a5f, #162b47); color: var(--accent); }
.ext-top-1 { background: linear-gradient(135deg, #1a3c2a, #142a1e); color: var(--accent-green); }
.ext-top-2 { background: linear-gradient(135deg, #3d2e15, #2a1f0e); color: #fbbf24; }
.ext-top-3 { background: linear-gradient(135deg, #2d1f4e, #1e1535); color: #a78bfa; }
.ext-top-4 { background: linear-gradient(135deg, #164040, #102a2a); color: #34d399; }
.ext-top-5 { background: linear-gradient(135deg, #3d1e1e, #2a1515); color: var(--accent-red); }
.ext-top-6 { background: linear-gradient(135deg, #3b1f55, #26153a); color: var(--accent-purple); }
.ext-top-7 { background: linear-gradient(135deg, #4a1a2e, #30141e); color: #fb7185; }
.ext-top-8 { background: linear-gradient(135deg, #0d3b38, #0a2725); color: #2dd4bf; }

/* ================================================================
   底部
   ================================================================ */
.dash-footer {
  max-width: 1200px; margin: 8px auto 0;
  text-align: center;
  font-size: 11px; color: rgba(255,255,255,0.15);
  display: flex; align-items: center; justify-content: center; gap: 4px; flex-wrap: wrap;
}
.ft-dot { margin: 0 2px; }
.ft-gap { width: 20px; }
.ft-live { color: rgba(74,222,128,0.5); display: flex; align-items: center; gap: 5px; }
.ft-pulse {
  display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--accent-green);
  box-shadow: 0 0 8px rgba(74,222,128,0.5);
  animation: ft-pulse 2s ease-in-out infinite;
}
@keyframes ft-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }

/* ================================================================
   响应式 — 平板 (768–1023)
   ================================================================ */
@media (max-width: 1023px) {
  .ext-grid { grid-template-columns: repeat(3, 1fr); }
  .shell-grid { grid-template-columns: repeat(2, 1fr); }
}

/* ================================================================
   响应式 — 手机横屏 / 小平板 (≤767px)
   ================================================================ */
@media (max-width: 767px) {
  .dash-root {
    padding: 10px 10px 24px;
    --gap: 14px;
    --gap-sm: 10px;
    --radius-xl: 16px;
    --radius-lg: 14px;
  }

  /* Hero */
  .dash-hero { border-radius: var(--radius-xl); }
  .hero-content { padding: 20px 18px; gap: 16px; }
  .hero-amount { font-size: 36px; }
  .hero-yen { font-size: 17px; }
  .hero-hb { font-size: 13px; }
  .hero-pill { font-size: 10px; padding: 2px 10px; }
  .hero-yest { margin-top: 4px; }
  .yest-num { font-size: 14px; }
  .sec-yest { font-size: 10px; }
  .hero-kpis { width: 100%; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
  .kpi-ring { width: 36px; height: 36px; }
  .kpi-num { font-size: 16px; }
  .kpi-label { font-size: 13px; }
  .hero-divider { height: 28px; }
  .hero-ctl { flex-direction: row; gap: 10px; width: 100%; justify-content: flex-end; }

  /* 内媒 */
  .intel-card-inner { padding: 20px; }
  .intel-num { font-size: 36px; }
  .intel-icon { width: 38px; height: 38px; }
  .intel-top-label { font-size: 14px; }
  .intel-top { margin-bottom: 14px; padding-bottom: 12px; }

  /* 外媒汇总卡片 */
  .extel-card-inner { padding: 20px; }
  .extel-num { font-size: 36px; }
  .extel-icon { width: 38px; height: 38px; }
  .extel-top-label { font-size: 14px; }
  .extel-top { margin-bottom: 14px; padding-bottom: 12px; }

  /* 外媒 */
  .ext-grid { grid-template-columns: repeat(2, 1fr); }
  .ext-val { font-size: 22px; }
  .ext-name { font-size: 12px; }
  .ext-top { padding: 14px; }
  .ext-hb { font-size: 11px; }
}

/* ================================================================
   响应式 — 手机竖屏 (≤480px)
   ================================================================ */
@media (max-width: 480px) {
  .dash-root {
    padding: 8px 8px 20px;
    --gap: 12px;
    --gap-sm: 8px;
  }

  /* Hero */
  .hero-content { padding: 16px 12px; gap: 12px; }
  .hero-amount { font-size: 28px; letter-spacing: -1px; }
  .hero-yen { font-size: 14px; }
  .hero-hb { font-size: 11px; }
  .hero-yest { margin-top: 2px; }
  .yest-num { font-size: 13px; }
  .yest-label { font-size: 10px; }
  .hero-head { width: 100%; }
  .hero-sub-row { flex-wrap: wrap; gap: 8px; margin-top: 6px; }
  .hero-kpis { gap: 8px; }
  .kpi-ring { width: 30px; height: 30px; }
  .kpi-dot-int, .kpi-dot-ext { width: 7px; height: 7px; }
  .kpi-num { font-size: 14px; }
  .kpi-label { font-size: 12px; letter-spacing: 2px; }
  .hero-divider { display: none; }
  .hero-ctl { width: 36px; flex-direction: column; gap: 4px; }
  .hero-clock { font-size: 10px; }
  .hero-refresh { width: 30px; height: 30px; font-size: 14px; }

  /* 骨架屏 */
  .shell-hero { padding: 14px; }
  .shell-block-lg { width: 120px; height: 26px; }

  /* 内媒 */
  .intel-card-inner { padding: 16px; }
  .intel-num { font-size: 28px; }
  .intel-body { flex-direction: column; align-items: flex-start; gap: 10px; }
  .intel-hb { font-size: 12px; }
  .intel-icon { width: 34px; height: 34px; border-radius: 10px; }
  .intel-icon svg { width: 18px; height: 18px; }
  .intel-top-label { font-size: 13px; }
  .intel-top { margin-bottom: 10px; padding-bottom: 10px; }
  .intel-extra { gap: 16px; }

  /* 外媒汇总卡片 */
  .extel-card-inner { padding: 16px; }
  .extel-num { font-size: 28px; }
  .extel-body { flex-direction: column; align-items: flex-start; gap: 10px; }
  .extel-hb { font-size: 12px; }
  .extel-icon { width: 34px; height: 34px; border-radius: 10px; }
  .extel-icon svg { width: 18px; height: 18px; }
  .extel-top-label { font-size: 13px; }
  .extel-top { margin-bottom: 10px; padding-bottom: 10px; }
  .extel-extra { gap: 16px; }

  /* 外媒 */
  .ext-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
  .ext-val { font-size: 18px; }
  .ext-name { font-size: 11px; }
  .ext-top { padding: 12px; }
  .ext-cell { border-radius: var(--radius-sm); }

  .sec-badge { font-size: 11px; padding: 3px 10px; }
  .sec-title { font-size: 11px; }

  .dash-footer { font-size: 10px; }
  .shell-grid-2 { grid-template-columns: 1fr; }
}
</style>



// WEBPACK FOOTER //
// src/components/login/weichiAllDataNew.vue